import classNames from '@/utils/classNames';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, FC, ReactNode } from 'react';
import styles from './Button.module.css';

interface BaseProps {
    isLoading?: boolean;
    icon?: ReactNode;
    trailingIcon?: boolean;
    kind?: 'primary' | 'secondary' | 'tertiary' | 'destruction';
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, BaseProps {
    href?: undefined;
}

interface AnchorProps extends AnchorHTMLAttributes<HTMLAnchorElement>, BaseProps {
    href: string;
}

type IProps = (ButtonProps | AnchorProps) & { disabled?: ButtonProps['disabled']; fake?: boolean };

export const Button: FC<IProps> = ({
    className,
    children,
    disabled,
    isLoading,
    icon,
    kind = 'secondary',
    trailingIcon = false,
    fake,
    ...rest
}) => {
    const iconElement = icon && (
        <span className={classNames(styles.icon, { [styles.loading]: isLoading })}>{icon}</span>
    );
    const content = (
        <>
            {!trailingIcon && iconElement}
            {children && <span>{children}</span>}
            {trailingIcon && iconElement}
        </>
    );
    const props = {
        className: classNames(
            styles.button,
            'cds-button',
            `cds-button--${kind}`,
            { 'cds-disabled': disabled },
            { 'cds-button--icon': !children },
            className
        ),
        ...rest,
    };

    if (fake) {
        // If fake is true, return a span to simulate a button without functionality
        return <span className={props.className}>{content}</span>;
    }

    if ('href' in props && props.href) {
        // If href is provided, render as an anchor element
        return <a {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}>{content}</a>;
    }

    return (
        <button disabled={disabled} {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}>
            {content}
        </button>
    );
};
